import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  X, Save, Trash2, Users, Clock, MapPin, StickyNote, CalendarClock, Tag,
  Search, Check, Plus,
} from 'lucide-react';
import { api } from '@/lib/api';
import { ADMIN } from '@/constants/testIds';
import ConfirmDialog from '@/components/admin/ConfirmDialog';

const SESSION_TYPES = ['1-on-1', 'Group', 'Lecture', 'Workshop', 'Other'];
const SESSION_STATUSES = ['New', 'Confirmed', 'Active', 'Completed', 'Expired', 'Cancelled'];
const DURATIONS = [30, 45, 60, 75, 90, 120];

const STATUS_META = {
  New:       { color: 'bg-white/10 text-white/85 border-white/15', help: 'Draft \u2014 no invoices yet. Confirm to invoice each client.' },
  Confirmed: { color: 'bg-[var(--pg-admin-accent-soft)] text-[var(--pg-admin-accent)] border-[var(--pg-admin-accent-ring)]', help: 'Invoiced. Will auto-activate at start time.' },
  Active:    { color: 'bg-green-500/15 text-green-300 border-green-500/30', help: 'Session is happening right now.' },
  Completed: { color: 'bg-green-500/25 text-green-200 border-green-500/40', help: 'Session finished.' },
  Expired:   { color: 'bg-red-500/15 text-red-300 border-red-500/30', help: 'Start time passed while status was New \u2014 slot is now reusable.' },
  Cancelled: { color: 'bg-white/5 text-white/60 border-white/10', help: 'Session cancelled.' },
};

const nowRoundedIso = () => {
  const d = new Date();
  d.setMinutes(Math.round(d.getMinutes() / 30) * 30, 0, 0);
  return d;
};

const toLocalInput = (isoOrDate) => {
  const d = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 16);
};

const fromLocalInput = (val) => new Date(val).toISOString();

const emptySession = (defaultStart) => ({
  client_ids: [],
  session_name: '',
  session_type: '1-on-1',
  location: '',
  start_time: toLocalInput(defaultStart || nowRoundedIso()),
  duration_min: 60,
  total_price: 0,
  status: 'New',
  notes: '',
});

const money = (v) => `SAR ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const SessionDialog = ({ open, onClose, onSaved, existing, clients = [], defaultStart }) => {
  const isEdit = !!existing;
  const [form, setForm] = useState(emptySession(defaultStart));
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [nameFocused, setNameFocused] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [services, setServices] = useState([]);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get('/admin/services');
        if (mounted) setServices((data || []).filter((s) => s.active !== false));
      } catch (err) {
        console.warn('Failed to load services for session dialog', err);
      }
    })();
    return () => { mounted = false; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      const ids = existing.client_ids && existing.client_ids.length
        ? existing.client_ids
        : (existing.client_id ? [existing.client_id] : []);
      setForm({
        client_ids: ids,
        session_name: existing.session_name || '',
        session_type: existing.session_type || '1-on-1',
        location: existing.location || '',
        start_time: toLocalInput(existing.start_time),
        duration_min: existing.duration_min || 60,
        total_price: existing.total_price ?? existing.price ?? 0,
        status: existing.status || 'New',
        notes: existing.notes || '',
      });
    } else {
      setForm(emptySession(defaultStart));
    }
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, existing, defaultStart, onClose]);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get('/admin/sessions/name-suggestions');
        if (!mounted) return;
        const merged = [
          ...(data?.suggestions || []),
          ...(data?.recent || []).filter((r) => !(data?.suggestions || []).includes(r)),
        ];
        setSuggestions(merged);
      } catch (err) {
        console.warn('Failed to load session name suggestions', err);
      }
    })();
    return () => { mounted = false; };
  }, [open]);

  const selectedClients = useMemo(
    () => form.client_ids.map((id) => clients.find((c) => c.id === id)).filter(Boolean),
    [form.client_ids, clients],
  );

  const pricePerClient = useMemo(() => {
    const n = Math.max(1, form.client_ids.length);
    return Number(form.total_price || 0) / n;
  }, [form.total_price, form.client_ids.length]);

  const filteredClients = useMemo(() => {
    const term = clientSearch.trim().toLowerCase();
    if (!term) return clients;
    return clients.filter((c) => c.name.toLowerCase().includes(term) || (c.email || '').toLowerCase().includes(term));
  }, [clients, clientSearch]);

  const toggleClient = (id) => {
    setForm((f) => {
      const has = f.client_ids.includes(id);
      return { ...f, client_ids: has ? f.client_ids.filter((x) => x !== id) : [...f.client_ids, id] };
    });
  };
  const removeClient = (id) => setForm((f) => ({ ...f, client_ids: f.client_ids.filter((x) => x !== id) }));

  const filteredSuggestions = useMemo(() => {
    const term = (form.session_name || '').trim().toLowerCase();
    if (!term) return suggestions.slice(0, 8);
    return suggestions.filter((s) => s.toLowerCase().includes(term)).slice(0, 8);
  }, [form.session_name, suggestions]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.client_ids.length) { toast.error('Please select at least one client.'); return; }
    if (!form.session_name.trim()) { toast.error('Please provide a session name.'); return; }
    if (!form.start_time) { toast.error('Please pick a date/time.'); return; }
    const payload = {
      client_ids: form.client_ids,
      session_name: form.session_name.trim(),
      session_type: form.session_type,
      location: form.location || '',
      start_time: fromLocalInput(form.start_time),
      duration_min: parseInt(form.duration_min, 10) || 60,
      total_price: parseFloat(form.total_price) || 0,
      status: form.status || 'New',
      notes: form.notes || '',
    };
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/admin/sessions/${existing.id}`, payload);
        toast.success(payload.status === 'Confirmed' && existing.status !== 'Confirmed'
          ? 'Session confirmed & invoices created.'
          : 'Session updated.');
      } else {
        await api.post('/admin/sessions', payload);
        toast.success(payload.status === 'Confirmed'
          ? 'Session created & invoices generated.'
          : 'Session created.');
      }
      onSaved?.();
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save session.'); }
    finally { setSaving(false); }
  };

  const del = () => setConfirmDeleteOpen(true);
  const performDelete = async () => {
    if (!existing) return;
    setConfirmDeleteOpen(false);
    setDeleting(true);
    try {
      await api.delete(`/admin/sessions/${existing.id}`);
      toast.success('Session deleted.');
      onSaved?.();
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to delete.'); }
    finally { setDeleting(false); }
  };

  if (!open) return null;

  const statusMeta = STATUS_META[form.status] || STATUS_META.New;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <form
        data-testid={ADMIN.sessionDialog}
        onSubmit={submit}
        className="relative w-full sm:max-w-3xl rounded-t-2xl sm:rounded-2xl border border-white/10 bg-[#141414] shadow-2xl h-[92dvh] sm:h-auto sm:max-h-[94vh] overflow-hidden flex flex-col"
      >
        <div className="flex items-start justify-between px-5 sm:px-6 pt-5 sm:pt-6 pb-3 border-b border-white/5">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-widest text-[var(--pg-admin-accent)] font-semibold">
              {isEdit ? 'Edit session' : 'New session'}
            </div>
            <h3 className="font-display text-2xl sm:text-3xl mt-0.5 leading-tight truncate">
              {form.session_name || 'Coaching Session'}
            </h3>
            <div className="mt-1 text-xs text-white/60">
              {selectedClients.length
                ? `${selectedClients.length} client${selectedClients.length === 1 ? '' : 's'} \u00b7 ${money(pricePerClient)} each`
                : 'No clients selected yet'}
            </div>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="p-2 rounded-lg hover:bg-white/5 shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 sm:px-6 py-5 space-y-5">
          {/* Session name */}
          <Field label="Session name" icon={Tag}>
            <div className="relative">
              <input
                data-testid={ADMIN.sessionNameInput}
                value={form.session_name}
                onChange={(e) => setForm({ ...form, session_name: e.target.value })}
                onFocus={() => setNameFocused(true)}
                onBlur={() => setTimeout(() => setNameFocused(false), 150)}
                placeholder="e.g. Group Clinic, 1-on-1 Training..."
                className="pg-fld"
                autoComplete="off"
              />
              {nameFocused && filteredSuggestions.length > 0 && (
                <div className="absolute z-30 top-full left-0 right-0 mt-1 rounded-xl border border-white/10 bg-[#0f0f0f] shadow-2xl overflow-hidden">
                  {filteredSuggestions.map((s) => (
                    <button
                      type="button"
                      key={s}
                      onMouseDown={(e) => { e.preventDefault(); setForm((f) => ({ ...f, session_name: s })); setNameFocused(false); }}
                      className="w-full text-left px-3 py-2 text-sm text-white/85 hover:bg-white/5 flex items-center gap-2"
                    >
                      <Plus className="w-3.5 h-3.5 text-white/40" /> {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </Field>

          {/* Clients */}
          <Field label={`Clients ${form.client_ids.length ? `(${form.client_ids.length})` : ''}`} icon={Users}>
            {/* Selected chips */}
            {selectedClients.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {selectedClients.map((c) => (
                  <span
                    key={c.id}
                    data-testid={ADMIN.sessionClientChip(c.id)}
                    className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-full bg-[var(--pg-admin-accent)] text-[var(--pg-admin-accent-fg)] text-xs font-medium"
                  >
                    {c.name}
                    <button
                      type="button"
                      onClick={() => removeClient(c.id)}
                      className="w-5 h-5 rounded-full inline-flex items-center justify-center hover:bg-black/15"
                      aria-label={`Remove ${c.name}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {/* Search + list */}
            <div className="rounded-xl border border-white/10 bg-black/30" data-testid={ADMIN.sessionClientPicker}>
              <div className="relative border-b border-white/5">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  data-testid={ADMIN.sessionClientSearch}
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  placeholder="Search clients..."
                  className="w-full bg-transparent pl-9 pr-3 py-2.5 text-sm outline-none text-white placeholder-white/40"
                />
              </div>
              <div className="max-h-40 overflow-y-auto py-1">
                {filteredClients.length === 0 ? (
                  <div className="px-3 py-3 text-xs text-white/50">No clients match your search.</div>
                ) : filteredClients.map((c) => {
                  const selected = form.client_ids.includes(c.id);
                  return (
                    <button
                      type="button"
                      key={c.id}
                      data-testid={ADMIN.sessionClientOption(c.id)}
                      onClick={() => toggleClient(c.id)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-white/5 transition-colors ${selected ? 'bg-white/[0.04]' : ''}`}
                    >
                      <span className={`w-5 h-5 rounded-md border shrink-0 inline-flex items-center justify-center ${selected ? 'bg-[var(--pg-admin-accent)] border-[var(--pg-admin-accent)]' : 'border-white/25'}`}>
                        {selected && <Check className="w-3 h-3 text-[var(--pg-admin-accent-fg)]" />}
                      </span>
                      <span className="flex-1 min-w-0 truncate">{c.name}</span>
                      <span className="text-[10px] uppercase tracking-widest text-white/50 shrink-0">{c.level}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </Field>

          {/* Grid: type / location / start / duration */}
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Session type">
              <select data-testid={ADMIN.sessionTypeSelect} value={form.session_type} onChange={(e) => {
                const val = e.target.value;
                const svc = services.find((s) => s.title === val);
                setForm((f) => ({
                  ...f,
                  session_type: val,
                  duration_min: svc?.duration_min || f.duration_min,
                  total_price: (svc?.default_price ?? f.total_price),
                }));
              }} className="pg-fld">
                {services.length === 0 && <option value="">— Add a service in Services &amp; Sessions —</option>}
                {services.map((s) => (
                  <option key={s.id} value={s.title}>{s.title}{s.default_price ? `  ·  SAR ${s.default_price}` : ''}{s.duration_min ? `  ·  ${s.duration_min} min` : ''}</option>
                ))}
                {form.session_type && !services.find((s) => s.title === form.session_type) && (
                  <option value={form.session_type}>{form.session_type} (legacy)</option>
                )}
              </select>
            </Field>
            <Field label="Location" icon={MapPin}>
              <input data-testid={ADMIN.sessionLocationInput} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Court / Club" className="pg-fld" />
            </Field>
            <Field label="Starts at" icon={CalendarClock}>
              <input data-testid={ADMIN.sessionStartInput} type="datetime-local" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="pg-fld" />
            </Field>
            <Field label="Duration" icon={Clock}>
              <select data-testid={ADMIN.sessionDurationInput} value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: parseInt(e.target.value, 10) })} className="pg-fld">
                {DURATIONS.map((d) => <option key={d} value={d}>{d} min</option>)}
              </select>
            </Field>
          </div>

          {/* Pricing */}
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Total price (SAR)">
              <div className="relative">
                <input
                  data-testid={ADMIN.sessionPriceInput}
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.total_price}
                  onChange={(e) => setForm({ ...form, total_price: e.target.value })}
                  className="pg-fld pl-12"
                  placeholder="0.00"
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-white/50 select-none">SAR</span>
              </div>
            </Field>
            <Field label="Price per client (auto)">
              <div
                data-testid={ADMIN.sessionPricePerClient}
                className="pg-fld pg-fld-readonly flex items-center justify-between"
              >
                <span>{money(pricePerClient)}</span>
                <span className="text-[10px] uppercase tracking-widest text-white/40">
                  {form.client_ids.length ? `\u00f7 ${form.client_ids.length} client${form.client_ids.length === 1 ? '' : 's'}` : 'split when clients added'}
                </span>
              </div>
            </Field>
          </div>

          {/* Status */}
          <Field label="Status">
            <div className="flex items-center gap-2">
              <select data-testid={ADMIN.sessionStatusSelect} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="pg-fld flex-1">
                {SESSION_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] uppercase tracking-widest ${statusMeta.color}`}>
                {form.status}
              </span>
            </div>
            <p className="mt-1.5 text-[11px] text-white/50">{statusMeta.help}</p>
          </Field>

          {/* Notes */}
          <Field label="Notes" icon={StickyNote}>
            <textarea data-testid={ADMIN.sessionNotesInput} rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Focus areas, plan, anything to remember..." className="pg-fld resize-none" />
          </Field>
        </div>

        {/* Sticky footer */}
        <div className="border-t border-white/5 px-5 sm:px-6 py-3 flex flex-wrap items-center justify-end gap-2 bg-black/20">
          {isEdit && (
            <button type="button" data-testid={ADMIN.sessionDeleteBtn} onClick={del} disabled={deleting} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-300 text-sm mr-auto">
              <Trash2 className="w-4 h-4" /> {deleting ? 'Deleting...' : 'Delete'}
            </button>
          )}
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl border border-white/10 hover:bg-white/5 text-sm">Cancel</button>
          <button
            data-testid={ADMIN.sessionSaveBtn}
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[var(--pg-admin-accent)] hover:bg-[var(--pg-admin-accent-hover)] disabled:opacity-60 text-[var(--pg-admin-accent-fg)] font-semibold px-4 py-2 rounded-xl transition-colors"
          >
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : (isEdit ? 'Update session' : 'Create session')}
          </button>
        </div>

        <style>{`
          .pg-fld{width:100%;border:1px solid rgba(255,255,255,.1);background:rgba(0,0,0,.3);border-radius:.75rem;padding:.6rem .9rem;color:#fff;outline:none;transition:border-color .15s;}
          .pg-fld:focus{border-color:var(--pg-admin-accent);box-shadow:0 0 0 3px var(--pg-admin-accent-ring);}
          .pg-fld:disabled{opacity:.5;}
          .pg-fld-readonly{cursor:default;background:rgba(255,255,255,.03);}
        `}</style>
      </form>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title="Delete this session?"
        description="Any invoices already generated for this session will be voided (kept for your audit trail)."
        confirmText={deleting ? 'Deleting...' : 'Delete session'}
        destructive
        onConfirm={performDelete}
      />
    </div>
  );
};

const Field = ({ label, icon: Icon, children }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-widest text-white/60 font-semibold inline-flex items-center gap-1.5">
      {Icon && <Icon className="w-3 h-3" />} {label}
    </span>
    <div className="mt-1.5">{children}</div>
  </label>
);

export default SessionDialog;
